// Komponen cetak struk thermal (58mm / 80mm) — layout ringkas ala kasir.
// Dipakai untuk template jenis "struk" ATAU ukuran_kertas thermal_* (lihat
// `isStruk` di FakturPrint.jsx, satu sumber untuk keputusan pemilihan
// komponen dipakai bersama oleh FakturSettings & FakturPublik).
//
// Semua data datang dari props — komponen ini TIDAK pernah fetch sendiri.
import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { rp, cetak, normalisasiEntitas, normalisasiItems, ringkasTotalDokumen, barisBayarCetak } from "./FakturPrint";

const LEBAR_MM = { thermal_58: 58, thermal_80: 80 };

export default function StrukPrint({
  data, template, publik = false, noFaktur, csatLink, tampilkanTombolCetak = true,
}) {
  const entity = normalisasiEntitas(data);
  const tpl = template || data?.template || {};
  const opsi = tpl.opsi || {};
  const kop = tpl.kop || {};
  const ukuran = tpl.ukuran_kertas || "thermal_80";
  const lebar = LEBAR_MM[ukuran] || 80;
  const items = normalisasiItems(entity);
  const noFakturFinal = noFaktur || data?.no_faktur || entity.code || "-";
  const csat = csatLink || data?.csat_link || null;
  const tampilSku = opsi.tampil_sku !== false;
  const tampilImei = opsi.tampil_imei !== false;

  // Logo kop — struk sempit (58/80mm) jadi ukurannya dibatasi tinggi saja
  // (biar lebar menyesuaikan proporsi gambar). Sebelumnya struk tidak punya
  // logo sama sekali; kosong/gagal dimuat = tetap tanpa logo (perilaku lama).
  const [logoGagal, setLogoGagal] = useState(false);
  useEffect(() => { setLogoGagal(false); }, [kop.logo_url]);
  const tampilkanLogo = kop.tampil_logo !== false && !!kop.logo_url && !logoGagal;

  // PPN (keputusan owner 18 Agu 2026) — struk ber-PPN mencetak DPP & PPN
  // terpisah, dan TOTAL adalah DPP+PPN. Diskon nota tanpa PPN (brief 22 Sep
  // 2026) dicetak sebagai Subtotal/Diskon. Aturannya SATU dengan faktur —
  // `ringkasTotalDokumen` di FakturPrint.jsx — supaya faktur dan struk untuk
  // nota yang sama tidak pernah menyebut total berbeda.
  const ringkas = ringkasTotalDokumen(entity, items);
  // Baris pembayaran (N3) — sumber yang SAMA dengan faktur.
  const bayarCetak = barisBayarCetak(entity, opsi);

  return (
    <div className="w-full">
      <style>{`@media print { @page { size: ${lebar}mm auto; margin: 3mm; } }`}</style>

      {tampilkanTombolCetak && (
        <div className="no-print flex justify-end mb-3">
          <button type="button" onClick={cetak} data-testid="btn-cetak-struk"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
            <Printer className="w-4 h-4" /> Cetak
          </button>
        </div>
      )}

      <div
        data-testid="struk-print-area"
        style={{ width: `${lebar}mm` }}
        className="print-area mx-auto bg-white border border-slate-200 rounded-xl shadow-sm p-3 font-mono text-[11px] leading-snug text-slate-900"
      >
        <div className="text-center mb-2">
          {tampilkanLogo && (
            <img
              src={kop.logo_url}
              alt={kop.judul || "Logo"}
              data-testid="struk-logo-gambar"
              onError={() => setLogoGagal(true)}
              className="mx-auto mb-1 object-contain"
              style={{ maxHeight: "12mm", maxWidth: "80%" }}
            />
          )}
          <p className="font-bold text-sm">{kop.judul || "STRUK PENJUALAN"}</p>
          {kop.alamat && <p className="text-[10px] text-slate-600">{kop.alamat}</p>}
          {kop.telepon && <p className="text-[10px] text-slate-600">Telp: {kop.telepon}</p>}
        </div>

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        <div className="flex justify-between text-[10px]">
          <span data-testid="struk-no-dokumen">{noFakturFinal}</span>
          <span>{entity.tanggal || "-"}{entity.waktu ? ` ${entity.waktu}` : ""}</span>
        </div>
        {entity.nama_pelanggan && <p className="text-[10px]">Plg: {entity.nama_pelanggan}</p>}
        {entity.nama_penjual && <p className="text-[10px]">Kasir: {entity.nama_penjual}</p>}

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        {items.length === 0 ? (
          <p className="text-center text-slate-400 py-2">Tidak ada barang</p>
        ) : items.map((it) => (
          <div key={it.key} className="mb-1" data-testid={`struk-item-${it.key}`}>
            <p className="truncate">
              {it.nama}
              {tampilImei && it.imeiList.length > 0 ? ` (${it.imeiList[0]})` : ""}
            </p>
            <div className="flex justify-between text-[10px] text-slate-600">
              <span>{it.qty} x {rp(it.harga)}{tampilSku && it.sku ? ` · ${it.sku}` : ""}</span>
              <span>{rp(it.total)}</span>
            </div>
            {/* Diskon per barang (N2) — aturan yang sama dengan faktur (sejak
                26f juga nota ber-PPN). */}
            {it.diskon > 0 && (
              <div className="flex justify-between text-[10px] text-slate-600"
                   data-testid={`struk-item-${it.key}-diskon`}>
                <span>Diskon</span><span>-{rp(it.diskon)}</span>
              </div>
            )}
          </div>
        ))}

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        {/* Urutan sama dengan faktur: potongan dulu, baru DPP & PPN (26f). */}
        {ringkas.diskon > 0 && (
          <div className="text-[10px] text-slate-600" data-testid="struk-diskon-breakdown">
            <div className="flex justify-between">
              <span>Subtotal</span><span data-testid="struk-subtotal">{rp(ringkas.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>Diskon</span><span data-testid="struk-diskon">-{rp(ringkas.diskon)}</span>
            </div>
          </div>
        )}
        {ringkas.kenaPpn && (
          <div className="text-[10px] text-slate-600" data-testid="struk-ppn-breakdown">
            <div className="flex justify-between"><span>DPP</span><span>{rp(ringkas.dpp)}</span></div>
            <div className="flex justify-between">
              <span>PPN ({entity.ppn_persen ?? 11}%)</span>
              <span data-testid="struk-ppn-nominal">{rp(ringkas.ppn)}</span>
            </div>
          </div>
        )}
        <div className="flex justify-between font-bold text-sm">
          <span>TOTAL</span>
          <span data-testid="struk-total">{rp(ringkas.total)}</span>
        </div>

        {bayarCetak ? (bayarCetak.length > 0 && (
          <div className="mt-1" data-testid="struk-bayar">
            {bayarCetak.map((b) => (
              <div key={b.key} className="text-[10px] text-slate-600"
                data-testid={b.jenis === "dp" ? "struk-dp" : `struk-bayar-${b.key}`}>
                <div className="flex justify-between gap-2">
                  <span className="truncate">{b.label}</span><span>{rp(b.nominal)}</span>
                </div>
                {b.imei && <p className="truncate">IMEI {b.imei}</p>}
                {b.keterangan && <p className="truncate">{b.keterangan}</p>}
              </div>
            ))}
          </div>
        )) : entity.payments && entity.payments.length > 0 && (
          <div className="mt-1">
            {entity.payments.map((p, i) => (
              <div key={i} className="flex justify-between text-[10px] text-slate-600">
                <span>{p.method}</span><span>{rp(p.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {opsi.tampil_catatan !== false && entity.catatan && (
          <p className="text-[10px] text-slate-600 mt-1.5">{entity.catatan}</p>
        )}

        {/* Catatan penjual — tetap milik TEMPLATE, berdampingan dengan catatan
            transaksi di atas (bukan menggantikannya). */}
        {tpl.catatan_penjual && tpl.catatan_penjual.trim() && (
          <p className="text-[10px] text-slate-600 mt-1.5">{tpl.catatan_penjual}</p>
        )}

        {/* Tautan CSAT — QR dibuat backend (csat.qr_data_url), TIDAK memuat
            dari layanan pihak ketiga di internet. Kalau backend belum sempat
            membuatkan QR, jangan sembunyikan diam-diam — tetap tampilkan
            tautannya sebagai teks. */}
        {opsi.sertakan_link_csat && csat?.survey_url && (
          <>
            <div className="border-t border-dashed border-slate-400 my-1.5" />
            <p className="text-center text-[10px]">Nilai pelayanan kami:</p>
            {csat.qr_data_url ? (
              <img
                src={csat.qr_data_url}
                alt="QR penilaian pelayanan" data-testid="struk-csat-qr"
                className="block mx-auto my-1"
                style={{ width: "22mm", height: "22mm" }}
              />
            ) : (
              <p className="text-center text-[9px] text-slate-500">(Kode QR belum tersedia)</p>
            )}
            <p className="text-center text-[9px] break-all">{csat.survey_url}</p>
          </>
        )}

        {tpl.footer_teks && (
          <>
            <div className="border-t border-dashed border-slate-400 my-1.5" />
            <p className="text-center text-[10px]">{tpl.footer_teks}</p>
          </>
        )}

        {/* Syarat & Ketentuan — blok terpisah dari footer_teks, di bagian
            paling bawah struk (fine print). Thermal panjangnya tak terbatas
            jadi cukup diberi jarak & garis putus-putus, tanpa perlu dipotong. */}
        {tpl.syarat_ketentuan && tpl.syarat_ketentuan.trim() && (
          <>
            <div className="border-t border-dashed border-slate-400 my-1.5" />
            <p className="text-center text-[9px] font-semibold text-slate-600">Syarat &amp; Ketentuan</p>
            <p className="text-[9px] text-slate-500 whitespace-pre-line">{tpl.syarat_ketentuan}</p>
          </>
        )}

        {publik && (
          <p className="text-center text-[9px] text-slate-300 mt-1.5">Dokumen dibuat otomatis oleh sistem.</p>
        )}
      </div>
    </div>
  );
}
